import { useForm } from '@tanstack/react-form'
import { servicioSchema, DEFAULT_SERVICIO_VALUES, type ServicioFormValues } from './servicioSchema'

export interface UseServicioFormOptions {
  defaultValues?: Partial<ServicioFormValues>
  onSubmit?: (data: ServicioFormValues) => Promise<void> | void
}

/**
 * Hook para manejar el formulario de Servicio con TanStack Form + Zod
 *
 * @example
 * const { form } = useServicioForm({
 *   defaultValues: servicio ? mapServicioToForm(servicio) : undefined,
 *   onSubmit: async (data) => {
 *     await crearServicio(data)
 *   }
 * })
 */
export function useServicioForm(options: UseServicioFormOptions = {}) {
  const { defaultValues, onSubmit } = options

  const form = useForm({
    defaultValues: {
      ...DEFAULT_SERVICIO_VALUES,
      ...defaultValues,
    } as ServicioFormValues,
    validators: {
      onSubmit: ({ value }) => {
        const result = servicioSchema.safeParse(value)
        if (!result.success) {
          return result.error.issues.map((issue) => ({
            message: issue.message,
            path: issue.path,
          }))
        }
        return undefined
      },
    },
    onSubmit: async ({ value }) => {
      if (onSubmit) {
        await onSubmit(value as ServicioFormValues)
      }
    },
  })

  return { form }
}

/**
 * Helper para extraer mensaje de error de TanStack Form
 * Compatible con Zod v4 (StandardSchemaV1Issue)
 */
export function getFieldError(errors: unknown[]): string | undefined {
  const first = errors[0]
  if (!first) return undefined
  if (typeof first === 'string') return first
  if (typeof first === 'object' && first !== null && 'message' in first) {
    return (first as { message: string }).message
  }
  return undefined
}

// Tipos exportados para uso en componentes
export type UseServicioFormReturn = ReturnType<typeof useServicioForm>
export type ServicioForm = UseServicioFormReturn['form']
